import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import {
    Button,
    Spinner,
    useToast,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SelectedGameContext from '../context/SelectedGameContext';
import { Timeout } from '../util/types';
import { AssetFileType, BuildData, getDepotList } from '../util/depot';
import { DepotBrowser } from '../components/Depot';
import { BranchData, BranchType, getBranchList } from '../util/branch';
import { BranchBrowser } from '../components/Branch';
import {
    ProfileError,
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';

export const ActorAssets: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const { keycloak } = useKeycloak();
    const [publicDepots, setPublicDepots] = useState<BuildData[]>([]);
    const [depots, setDepots] = useState<BuildData[]>([]);
    const [depotsLoaded, setDepotsLoaded] = useState(false);
    const [branches, setBranches] = useState<BranchData[]>([]);
    const [branchesLoaded, setBranchesLoaded] = useState(false);
    const toast = useToast();
    const currLicense = selectedGame?.currLicense;
    const timeoutRef = useRef<Timeout>();
    const timeoutRef2 = useRef<Timeout>();
    const areDepotsLoaded = useRef(false);
    const controller = new AbortController();
    const signal = controller.signal;
    const [errors, setErrors] = useState<ProfileError[]>([]);

    const depotChecker = () => {
        if (!areDepotsLoaded.current) {
            console.log('Experiencing a slow response time...');
            return toast({
                title: 'Still Loading',
                description: 'Hmm, this is taking longer than expected...',
                status: 'warning',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
        }
    };

    const cancelCall = () => {
        if (!areDepotsLoaded.current) {
            setErrors((errors) => errors.concat(ProfileError.REQUEST_ABORTED));
            console.log('Request aborted.');
            controller.abort();
            return toast({
                title: 'Request Timed Out',
                description: "We're sorry! please try again later.",
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        setErrors([]);
    }, []);

    useEffect(() => {
        areDepotsLoaded.current = depotsLoaded;
    }, [depotsLoaded]);

    useEffect(() => {
        timeoutRef.current = setTimeout(depotChecker, 9000);
        timeoutRef2.current = setTimeout(cancelCall, 30000);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (timeoutRef2.current) clearTimeout(timeoutRef2.current);
        };
    }, []);

    useEffect(() => {
        if (!currLicense) {
            return;
        }
        getDepotInformation();
    }, [currLicense]);

    const getDepotInformation = async () => {
        setDepotsLoaded(false);
        setBranchesLoaded(false);

        try {
            const builds = await getDepotList(currLicense?.key, keycloak.token);
            // filter out rmb builds
            const actorBuilds = builds.filter(
                (build: any) => !build.fileTypes.includes(AssetFileType.rmb),
            );
            setDepots(actorBuilds);

            const publicBuilds = await getDepotList('public', keycloak.token);
            // filter for only rmb builds
            const publicRmbBuilds = publicBuilds.filter((build: any) =>
                build.fileTypes.includes(AssetFileType.rmb),
            );
            setPublicDepots(publicRmbBuilds);

            const devBranches = await getBranchList(currLicense?.key, keycloak.token);
            setBranches(devBranches);

            setDepotsLoaded(true);
            setBranchesLoaded(true);
        } catch (err: any) {
            setDepotsLoaded(true);
            setBranchesLoaded(true);
            return toast({
                title: 'Error',
                description: err.toString(),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    if (!currLicense && errors.length === 0) {
        return (
            <Center width="100%" height="100%">
                <Spinner data-cy="loading" />
            </Center>
        );
    }
    return (
        <Box>
            <Heading>Manage Unity / CSharp Actors</Heading>
            <br />
            {errors.length > 0 ? (
                <>
                    <Heading textAlign={'center'} marginBottom={'1em'}>
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : (
                <>
                    <Heading as="h2" size="md">
                        Branches
                    </Heading>
                    {branchesLoaded ? (
                        <BranchBrowser
                            showActorBuild={true}
                            branches={branches}
                            refreshPage={getDepotInformation}
                        />
                    ) : (
                        <Center width="100%" height="100%">
                            <Spinner data-cy="loading" />
                        </Center>
                    )}

                    <br />
                    <br />

                    <Accordion allowToggle allowMultiple defaultIndex={[0]}>
                        <AccordionItem>
                            <AccordionButton>
                                <Box as="span" flex="1" textAlign="left">
                                    <Heading as="h2" size="md">
                                        Developer Actors
                                    </Heading>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel>
                                <br />
                                <Flex>
                                    <Link
                                        to={`/game/${currLicense?.key}/csharp-actor/add-unity-actor-depot`}
                                    >
                                        <Button>Upload Unity Actor</Button>
                                    </Link>
                                    <Link
                                        to={`/game/${currLicense?.key}/csharp-actor/add-csharp-actor-depot`}
                                    >
                                        <Button>Upload C# Actor</Button>
                                    </Link>
                                </Flex>
                                <br />
                                {depotsLoaded ? (
                                    <DepotBrowser
                                        isPublic={false}
                                        depots={depots}
                                        branches={branches}
                                        branchType={BranchType.actor}
                                        signal={signal}
                                        refreshPage={getDepotInformation}
                                    />
                                ) : (
                                    <Center width="100%" height="100%">
                                        <Spinner data-cy="loading" />
                                    </Center>
                                )}
                            </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem>
                            <AccordionButton>
                                <Box as="span" flex="1" textAlign="left">
                                    <Heading as="h2" size="md">
                                        Public RMBs
                                    </Heading>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel>
                                {depotsLoaded ? (
                                    <DepotBrowser
                                        isPublic={true}
                                        depots={publicDepots}
                                        branches={branches}
                                        branchType={BranchType.rmb}
                                        signal={signal}
                                        refreshPage={getDepotInformation}
                                    />
                                ) : (
                                    <Center width="100%" height="100%">
                                        <Spinner data-cy="loading" />
                                    </Center>
                                )}
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </>
            )}
        </Box>
    );
};
